/**
 * Cache Service - Comprehensive caching layer for the Dating App
 * Combines session storage, in-memory cache, and smart invalidation
 */

class CacheService {
  constructor() {
    // In-memory cache storage
    this.memoryCache = new Map();

    // Cache metadata (timestamps, TTL)
    this.metadata = new Map();

    // Session storage prefix
    this.SESSION_PREFIX = "dating_app_cache_";

    // Default cache TTL in milliseconds
    this.DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

    // Initialize cleanup interval
    this.startCleanupInterval();
  }

  /**
   * Set a cache value with optional TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {Object} options - { ttl, useSessionStorage }
   */
  set(key, value, options = {}) {
    const { ttl = this.DEFAULT_TTL, useSessionStorage = false } = options;
    const timestamp = Date.now();

    // Store in memory cache
    this.memoryCache.set(key, value);

    // Store metadata
    this.metadata.set(key, {
      timestamp,
      ttl,
      useSessionStorage,
    });

    // Also store in session storage if requested
    if (useSessionStorage) {
      try {
        const data = {
          value,
          timestamp,
          ttl,
        };
        sessionStorage.setItem(this.SESSION_PREFIX + key, JSON.stringify(data));
      } catch (error) {
        console.warn(
          `[Cache] Failed to set session storage for ${key}:`,
          error,
        );
      }
    }
  }

  /**
   * Get a cached value
   * @param {string} key - Cache key
   * @returns {any|null} - Cached value or null if expired/not found
   */
  get(key) {
    const now = Date.now();
    const meta = this.metadata.get(key);

    // Check if in memory and not expired
    if (this.memoryCache.has(key) && meta) {
      if (now - meta.timestamp < meta.ttl) {
        return this.memoryCache.get(key);
      } else {
        // Cache expired, remove it
        this.remove(key);
        return null;
      }
    }

    // Try to load from session storage if not in memory
    if (meta?.useSessionStorage) {
      try {
        const stored = sessionStorage.getItem(this.SESSION_PREFIX + key);
        if (stored) {
          const { value, timestamp, ttl } = JSON.parse(stored);
          if (now - timestamp < ttl) {
            // Restore to memory cache
            this.memoryCache.set(key, value);
            return value;
          } else {
            // Expired in session storage too
            sessionStorage.removeItem(this.SESSION_PREFIX + key);
          }
        }
      } catch (error) {
        console.warn(
          `[Cache] Failed to get session storage for ${key}:`,
          error,
        );
      }
    }

    return null;
  }

  /**
   * Check if a key exists and is not expired
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Remove a specific cache entry
   * @param {string} key - Cache key
   */
  remove(key) {
    this.memoryCache.delete(key);
    this.metadata.delete(key);

    try {
      sessionStorage.removeItem(this.SESSION_PREFIX + key);
    } catch (error) {
      console.warn(
        `[Cache] Failed to remove session storage for ${key}:`,
        error,
      );
    }
  }

  /**
   * Clear all cache (or by pattern)
   * @param {string|RegExp} pattern - Optional pattern to match keys
   */
  clear(pattern = null) {
    if (!pattern) {
      // Clear everything
      this.memoryCache.clear();
      this.metadata.clear();

      try {
        const keys = Object.keys(sessionStorage);
        keys.forEach((key) => {
          if (key.startsWith(this.SESSION_PREFIX)) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.warn("[Cache] Failed to clear session storage:", error);
      }
      return;
    }

    // Clear by pattern
    const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);

    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.remove(key);
      }
    }
  }

  /**
   * Get cache stats
   * @returns {Object}
   */
  getStats() {
    return {
      memoryCount: this.memoryCache.size,
      metadataCount: this.metadata.size,
      sessionStorageSize: this.getSessionStorageSize(),
    };
  }

  /**
   * Get session storage size in bytes
   * @returns {number}
   */
  getSessionStorageSize() {
    let size = 0;
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach((key) => {
        if (key.startsWith(this.SESSION_PREFIX)) {
          size += sessionStorage.getItem(key)?.length || 0;
        }
      });
    } catch (error) {
      console.warn("[Cache] Failed to calculate session storage size:", error);
    }
    return size;
  }

  /**
   * Start cleanup interval to remove expired entries
   * @private
   */
  startCleanupInterval() {
    // Cleanup every minute
    setInterval(() => {
      const now = Date.now();
      const keysToRemove = [];

      for (const [key, meta] of this.metadata.entries()) {
        if (now - meta.timestamp >= meta.ttl) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => this.remove(key));
    }, 60000);
  }

  /**
   * Batch get multiple keys
   * @param {string[]} keys - Array of cache keys
   * @returns {Object} - Map of key -> value
   */
  getBatch(keys) {
    const result = {};
    keys.forEach((key) => {
      const value = this.get(key);
      if (value !== null) {
        result[key] = value;
      }
    });
    return result;
  }

  /**
   * Batch set multiple entries
   * @param {Object} entries - Map of key -> value
   * @param {Object} options - Common options for all entries
   */
  setBatch(entries, options = {}) {
    Object.entries(entries).forEach(([key, value]) => {
      this.set(key, value, options);
    });
  }

  /**
   * Get or set pattern - like getOrSet
   * @param {string} key - Cache key
   * @param {Function} fetcher - Async function to fetch value if not cached
   * @param {Object} options - Cache options
   * @returns {Promise<any>}
   */
  async getOrSet(key, fetcher, options = {}) {
    // Try to get from cache first
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }

    // Not cached, fetch it
    const value = await fetcher();

    // Store in cache
    this.set(key, value, options);

    return value;
  }
}

// Export singleton instance
export const cacheService = new CacheService();

export default cacheService;
